var YBRequest = require('../request')
var Transform = require('./transform')
var Log = require('../../log')
var Util = require('../../util')
var Constants = require('../../constants')

var FastDataTransform = Transform.extend(
  /** @lends npaw.FastDataTransform.prototype */
  {
    /**
     * This class manages Fastdata service and view index.
     *
     * @constructs
     * @extends npaw.Transform
     * @memberof npaw
     *
     * @param {Plugin} plugin Instance of {@link Plugin}
     * @param {string} session If provided, plugin will use this as a FD response.
     */
    constructor: function (plugin, session) {
      Transform.prototype.constructor.apply(this, arguments)

      /** /data response */
      this.response = {}

      this._viewIndex = new Date().getTime()

      this._session = session

      this.plugin = plugin

      this._httpSecure = plugin.options['app.https']
      this._fastDataHost = this.plugin.getFastDataHost()

      this.transformName = 'FastData'

      /** Create interval to get configuration from LMA periodically */
      this._createConfigurationInterval()
    },

    /**
     * Create Configuration Interval
     * @private
     */
    _createConfigurationInterval: function () {
      // Register only if refresh lma configuration enabled, and is not created previously
      if (this.plugin.isRefreshLMAConfigurationEnabled() && !this._refreshInterval) {
        this._refreshInterval = setInterval(this.loadConfiguration.bind(this),
          this.plugin.refreshLMAConfigurationSeconds() * 1000)
      }
    },

    /**
     * Starts the 'FastData' fetching. This will send the initial request to NPAW in order to get
     * the needed info for the rest of the requests.
     *
     * This is an asynchronous process.
     *
     * When the fetch is complete, {@link #fastDataConfig} will contain the parsed info.
     * @see FastDataConfig
     */
    init: function () {
      // offline
      if (this.plugin.options && this.plugin.options.offline) {
        // set the options
        this.response.host = 'OFFLINE'
        this.response.code = 'OFFLINE'
        this.response.pingTime = 60
        this.response.beatTime = 60
        this.done()
        return null
      }

      // reusing old data not expired
      if (this.plugin.storage.isEnabled()) {
        var now = new Date().getTime()
        if (now < this.plugin.appAnalytics.sessionExpire + (Number(this.plugin.getDataTime()) || 0) &&
          this.plugin.getStoredData()) {
          this.setData(this.plugin.getStoredData())
          return null
        }
      }

      // request new data
      var serviceData = Constants.Service.DATA
      var dataParams = this.plugin.requestBuilder.buildParams({ outputformat: 'json' }, serviceData, [this.plugin])

      // load configuration from backend
      this.loadConfiguration()

      if (dataParams !== null) {
        try {
          Log.notice(serviceData + ' ' + dataParams.system)
          if (dataParams.system === 'nicetest') {
            // "nicetest" is the default accountCode.
            // If found here, it's very likely that the customer has forgotten to set it.
            Log.error('No accountCode has been set. Please set your accountCode inside plugin\'s options.')
          }

          new YBRequest(this._fastDataHost, serviceData, dataParams)
            .on(YBRequest.Event.SUCCESS, this._receiveData.bind(this))
            .on(YBRequest.Event.ERROR, this._failedData.bind(this))
            .send()
        } catch (err) {
          Log.error('Error doing get data request to FastData service.')
        }
      }
    },

    /**
     * Load COnfiguration from backend
     */
    loadConfiguration: function () {
      var serviceConfiguration = Constants.Service.CONFIGURATION
      var configurationParams = this.plugin.requestBuilder.buildParams({}, serviceConfiguration, [this.plugin])

      if (configurationParams !== null) {
        try {
          new YBRequest(this._fastDataHost, serviceConfiguration, configurationParams)
            .on(YBRequest.Event.SUCCESS, this._receiveConfiguration.bind(this))
            .on(YBRequest.Event.ERROR, this._failedConfiguration.bind(this))
            .send()
        } catch (err) {
          Log.error('Error doing get configuration request to FastData service.')
        }
      }
    },

    /**
     * Uses given response to set fastdata response.
     *
     * @param {String} response Fastdata response as json string.
     */
    setData: function (response) {
      try {
        var resp = JSON.parse(response)
        if (this.plugin.options['parse.fdsResponseHost']) {
          response = response.replace(resp.q.h, this.plugin.options['parse.fdsResponseHost'](resp.q.h))
          resp = JSON.parse(response)
        }
        this.response.msg = response
        this.response.host = Util.addProtocol(resp.q.h, this._httpSecure)
        this.response.code = resp.q.c
        this.response.pingTime = resp.q.pt || 5
        this.response.beatTime = resp.q.i ? resp.q.i.bt || 30 : 30
        this.response.sessionExpire = resp.q.i ? resp.q.i.exp || 120 : 120
        this.plugin.storage.setLocal('sessionExpire', this.response.sessionExpire)
        this.plugin.processFastDataResponse(this.response)
        this.done()
      } catch (err) {
        Log.error('Fastdata response is invalid.')
      }
    },

    /**
     *
     * @param response
     */
    setConfiguration: function (response) {
      try {
        this.response.configurationOptions = JSON.parse(response)
        this.emit(FastDataTransform.Event.CONFIGURATION_LOADED)
      } catch (err) {
        Log.error('Configuration response is invalid.')
      }
    },

    /**
     * Parse the response from the fastData service.
     *
     * @private
     */
    _receiveData: function (req, e) {
      var msg = req.getResponse()
      this.setData(msg)
    },

    _receiveConfiguration: function (req, e) {
      var msg = req.getResponse()
      this.setConfiguration(msg)
    },

    _failedData: function (req, e) {
      Log.error('Fastdata request has failed.')
    },

    _failedConfiguration: function (req, e) {
      Log.error('Fastdata configuration request has failed.')
    },

    /**
     * Returns the current session
     *
     * @returns {string} Session
     */
    getSession: function () {
      return this._session
    },

    /**
     * Sets the session
     *
     * @param {String} sessionId Sets the session
     */
    setSession: function (session) {
      this._session = session
    },

    /** Sets the host url
    *
    * @param {String} host Sets the host
    */
    setHost: function (host) {
      this.response.host = host
    },

    /**
     * Transform requests
     * @param {npaw.comm.YBRequest} request YBRequest to transform.
     */
    parse: function (request) {
      // Host, system and sessionRoot for all
      request.host = request.host || this.response.host
      request.params.system = this.plugin.getAccountCode()
      request.params.sessionRoot = request.params.sessionRoot || this.getSession()

      // PingTime for ping and start
      if (FastDataTransform.EventList.PingTime.indexOf(request.service) !== -1) {
        request.params.pingTime = request.params.pingTime || this.response.pingTime
      }

      // ViewCode for non appanalytics events
      if ((FastDataTransform.EventList.AppAnalytics.indexOf(request.service) === -1) && (FastDataTransform.EventList.PluginAppLogs.indexOf(request.service) === -1)) {
        if (this.plugin.videos.existsVideo(request.getVideoKey())) {
          request.params.code = request.params.code || this.plugin.videos.getVideo(request.getVideoKey()).getViewCode()
        }
      } else {
        // For appanalytics events, sessionId
        request.params.sessionId = request.params.sessionRoot
      }

      // For init error and start, parentId and navContext
      if (FastDataTransform.EventList.CreateView.indexOf(request.service) !== -1) {
        if (this.plugin.appAnalytics.appAnalyticsStarted || (this.plugin.storage.isEnabled() && this.plugin.storage.getLocal('appAnalyticsStarted'))) {
          request.params.parentId = request.params.sessionRoot
          request.params.navContext = request.params.navContext || this.plugin.appAnalytics.getContext()
        }
      }
    }
  },
  {
    // Static members
    EventList: {
      CreateView: [
        Constants.Service.START,
        Constants.Service.INIT,
        Constants.Service.ERROR
      ],
      AppAnalytics: [
        Constants.Service.NAV,
        Constants.Service.SESSION_START,
        Constants.Service.SESSION_STOP,
        Constants.Service.EVENT,
        Constants.Service.BEAT
      ],
      PingTime: [
        Constants.Service.START,
        Constants.Service.PING
      ],
      PluginAppLogs: [
        Constants.Service.APP_ANALYTICS_PLUGIN_LOGS
      ],
      PluginVideoLogs: [
        Constants.Service.VIDEO_PLUGIN_LOGS
      ]
    },
    Event: {
      DONE: 'done',
      CONFIGURATION_LOADED: 'configurationLoaded'
    }
  })

module.exports = FastDataTransform
